/**
 * Dollars over cars, one date axis: the "Not on the Website" page's chart.
 * Two panels stacked — the dollar total above, the car count beneath — each
 * with its own full-height scale, sharing the dates so a drop in one sits
 * directly above or below the drop in the other. Ian, 2026-10-08, rejecting
 * a two-scale chart: "the vehicle count is hard to gauge because its so small
 * in comparison to the evaluation. the increments are hard to judge."
 *
 * Dates are to scale: a day without a run is a gap, bridged with a dashed
 * segment so the line does not fall apart. Follows the dataviz rules for a
 * single series per panel: 2px line, no legend (the panel captions name the
 * series), a recessive grid, the last point marked and labelled, every other
 * point surfaced by hover with a crosshair across both panels, and a "View as
 * table" fold. The optional dashed vertical mark is the day the first morning
 * alert went out.
 */
import React, { useRef, useState } from "react"

import { THEME } from "../../../config/theme"

export interface StackedPoint {
  date: Date
  /** null on points saved before this dollar figure was recorded. */
  dollars: number | null
  /** null on points saved before this count was recorded for the view
   * (a store's sold listings, for one), so the line starts where the data does. */
  count: number | null
}

interface Props {
  points: StackedPoint[]
  color: string
  /** Panel headings, in full: "HomeNet Selling Price of those cars", "Cars not
   * on the website". Ian, 2026-10-08: a bare "CARS" was "not identifiable
   * enough" and "looked crammed in the middle". */
  dollarsLabel: string
  countLabel: string
  /** The count's unit for the end label and tooltip: "cars", "listings". */
  countUnit: string
  formatDollars: (value: number) => string
  ariaLabel: string
  alertsStartedAt?: Date | null
  /** Text shown in place of the plot when there are no points yet. */
  emptyText: string
  /** Drawing width in SVG units; a narrow column draws narrower so its
   * labels stay the same size on screen. */
  viewWidth?: number
}

const WIDE_VIEW_WIDTH = 820
const VIEW_HEIGHT = 380
const MARGIN = { left: 60, right: 18 }
/** The two panels' vertical bands, in SVG units, with room above each for
 * its heading so the lower one is not wedged between the plots. */
const DOLLARS_PANEL = { top: 26, bottom: 166 }
const COUNT_PANEL = { top: 222, bottom: 342 }
const DATE_LABEL_Y = VIEW_HEIGHT - 12
const GRID_LINES = 3
const DAY_MS = 86_400_000
/** A gap longer than this between runs is drawn dashed. Audits run about
 * once a day with the odd two-day gap, so only a longer silence is marked. */
const GAP_DAYS = 2.5
const AXIS_TEXT = THEME.colors.neutral.light.textSecondary

function formatDay(date: Date): string {
  return date.toLocaleDateString([], { month: "short", day: "numeric" })
}

/** [low, high] with a little headroom; counts and dollars never go below zero. */
function band(values: number[]): [number, number] {
  const low = Math.min(...values)
  const high = Math.max(...values)
  const pad = high === low ? Math.max(1, high * 0.1) : (high - low) * 0.12
  return [Math.max(0, low - pad), high + pad]
}

export default function RunStackedChart({
  points, color, dollarsLabel, countLabel, countUnit, formatDollars, ariaLabel,
  alertsStartedAt = null, emptyText, viewWidth = WIDE_VIEW_WIDTH,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  if (points.length === 0) {
    return <div style={{ padding: "28px 18px", fontSize: 12.5, color: AXIS_TEXT }}>{emptyText}</div>
  }

  const VIEW_WIDTH = viewWidth
  const PLOT_WIDTH = VIEW_WIDTH - MARGIN.left - MARGIN.right
  const firstTime = points[0].date.getTime()
  const lastTime = points[points.length - 1].date.getTime()
  const xAtTime = (time: number) =>
    lastTime === firstTime
      ? MARGIN.left + PLOT_WIDTH / 2
      : MARGIN.left + ((time - firstTime) / (lastTime - firstTime)) * PLOT_WIDTH
  const xAt = (index: number) => xAtTime(points[index].date.getTime())

  const dollarValues = points.map((p) => p.dollars).filter((v): v is number => v !== null)
  const [dollarsLow, dollarsHigh] = dollarValues.length > 0 ? band(dollarValues) : [0, 1]
  const countValues = points.map((p) => p.count).filter((v): v is number => v !== null)
  const [countLow, countHigh] = countValues.length > 0 ? band(countValues) : [0, 1]
  const yDollars = (value: number) =>
    DOLLARS_PANEL.top + (1 - (value - dollarsLow) / (dollarsHigh - dollarsLow)) * (DOLLARS_PANEL.bottom - DOLLARS_PANEL.top)
  const yCount = (value: number) =>
    COUNT_PANEL.top + (1 - (value - countLow) / (countHigh - countLow)) * (COUNT_PANEL.bottom - COUNT_PANEL.top)

  // Date labels: about four across the span, never two within 60% of a step.
  const spanDays = Math.max(1, (lastTime - firstTime) / DAY_MS)
  const labelStepDays = Math.max(1, Math.ceil(spanDays / 4))
  const labelledTimes: number[] = []
  for (const point of points) {
    const time = point.date.getTime()
    const lastLabelled = labelledTimes[labelledTimes.length - 1]
    const farFromLast = lastLabelled === undefined || time - lastLabelled >= labelStepDays * DAY_MS
    const farFromEnd = lastTime - time >= labelStepDays * DAY_MS * 0.6
    if (farFromLast && farFromEnd) labelledTimes.push(time)
  }

  const alertsX =
    alertsStartedAt !== null && alertsStartedAt.getTime() <= lastTime
      ? xAtTime(Math.max(alertsStartedAt.getTime(), firstTime))
      : null

  /** Solid segments between consecutive days, dashed across skipped days;
   * a null value breaks the line. */
  const paths = (valueOf: (p: StackedPoint) => number | null, yOf: (v: number) => number) => {
    let solid = ""
    let dashed = ""
    for (let i = 1; i < points.length; i += 1) {
      const previous = valueOf(points[i - 1])
      const current = valueOf(points[i])
      if (previous === null || current === null) continue
      const segment = `M${xAt(i - 1)},${yOf(previous)} L${xAt(i)},${yOf(current)} `
      if (points[i].date.getTime() - points[i - 1].date.getTime() > GAP_DAYS * DAY_MS) dashed += segment
      else solid += segment
    }
    return { solid, dashed }
  }
  const dollarPaths = paths((p) => p.dollars, yDollars)
  const countPaths = paths((p) => p.count, yCount)
  const lastIndex = points.length - 1
  const last = points[lastIndex]

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current
    if (svg === null) return
    const rect = svg.getBoundingClientRect()
    // A zero-width box (not laid out yet, or a test runner) has no point under the cursor.
    if (rect.width === 0) return
    const viewX = ((event.clientX - rect.left) / rect.width) * VIEW_WIDTH
    let nearest = 0
    for (let i = 1; i < points.length; i += 1) {
      if (Math.abs(xAt(i) - viewX) < Math.abs(xAt(nearest) - viewX)) nearest = i
    }
    setHoverIndex(nearest)
  }

  const hovered = hoverIndex === null ? null : points[hoverIndex]
  const tooltipLeft = hoverIndex === null ? 0 : (xAt(hoverIndex) / VIEW_WIDTH) * 100

  const renderGrid = (
    panel: { top: number; bottom: number }, low: number, high: number,
    yOf: (v: number) => number, format: (v: number) => string,
  ) =>
    Array.from({ length: GRID_LINES + 1 }, (_, step) => {
      const value = low + (step / GRID_LINES) * (high - low)
      const y = yOf(value)
      return (
        <g key={`${panel.top}-${step}`}>
          <line x1={MARGIN.left} x2={VIEW_WIDTH - MARGIN.right} y1={y} y2={y} stroke={THEME.colors.neutral.light.border} strokeWidth={1} />
          <text x={MARGIN.left - 8} y={y + 4} textAnchor="end" fontSize={11} fill={AXIS_TEXT}>{format(value)}</text>
        </g>
      )
    })

  const renderLine = (
    lines: { solid: string; dashed: string }, lastValue: number | null, yOf: (v: number) => number, label: string | null,
  ) => (
    <g>
      <path d={lines.dashed} fill="none" stroke={color} strokeWidth={2} strokeDasharray="3 4" strokeLinejoin="round" opacity={0.7} />
      <path d={lines.solid} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
      {lastValue !== null && (
        <>
          <circle cx={xAt(lastIndex)} cy={yOf(lastValue)} r={4} fill={color} stroke={THEME.colors.white} strokeWidth={2} />
          {/* The end value wears a white halo so it stays legible over the line. */}
          {label !== null && (
            <text
              x={xAt(lastIndex) - 10} y={yOf(lastValue) - 12} textAnchor="end" fontSize={12} fontWeight={600}
              fill={THEME.colors.neutral.light.textPrimary} stroke={THEME.colors.white} strokeWidth={3} paintOrder="stroke"
            >
              {label}
            </text>
          )}
        </>
      )}
    </g>
  )

  // A sentence-case sub-heading over each panel, in the primary ink so it
  // reads as the panel's name rather than another axis label.
  const panelCaption = (text: string, y: number) => (
    <text x={MARGIN.left} y={y} fontSize={13} fontWeight={600} fill={THEME.colors.neutral.light.textPrimary}>
      {text}
    </text>
  )

  return (
    <div>
      <div style={{ position: "relative", padding: "14px 18px 8px" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          role="img"
          aria-label={ariaLabel}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
          // Height follows the width at the drawing's own ratio, so a narrow
          // column (or a phone) does not letterbox the plot inside a fixed box.
          style={{ display: "block", width: "100%", height: "auto", fontFamily: THEME.fonts.primary }}
        >
          {panelCaption(dollarsLabel, DOLLARS_PANEL.top - 8)}
          {renderGrid(DOLLARS_PANEL, dollarsLow, dollarsHigh, yDollars, formatDollars)}
          {panelCaption(countLabel, COUNT_PANEL.top - 8)}
          {renderGrid(COUNT_PANEL, countLow, countHigh, yCount, (v) => String(Math.round(v)))}
          {labelledTimes.map((time) => (
            <text key={time} x={xAtTime(time)} y={DATE_LABEL_Y} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>
              {formatDay(new Date(time))}
            </text>
          ))}
          <text x={xAt(lastIndex)} y={DATE_LABEL_Y} textAnchor="end" fontSize={11} fill={AXIS_TEXT}>
            {formatDay(last.date)}
          </text>
          {alertsX !== null && (
            <g>
              <line x1={alertsX} x2={alertsX} y1={DOLLARS_PANEL.top} y2={COUNT_PANEL.bottom} stroke={AXIS_TEXT} strokeWidth={1} strokeDasharray="3 3" />
              {/* At the foot of the lower panel, away from the end-value labels. */}
              <text x={alertsX - 6} y={COUNT_PANEL.bottom - 8} textAnchor="end" fontSize={11} fill={AXIS_TEXT}>
                alerts start
              </text>
            </g>
          )}
          {renderLine(dollarPaths, last.dollars, yDollars, last.dollars === null ? null : formatDollars(last.dollars))}
          {renderLine(countPaths, last.count, yCount, last.count === null ? null : `${last.count} ${countUnit}`)}
          {hoverIndex !== null && hovered !== null && (
            <g>
              <line x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={DOLLARS_PANEL.top} y2={COUNT_PANEL.bottom} stroke={THEME.colors.silver} strokeWidth={1} />
              {hovered.dollars !== null && (
                <circle cx={xAt(hoverIndex)} cy={yDollars(hovered.dollars)} r={4} fill={color} stroke={THEME.colors.white} strokeWidth={2} />
              )}
              {hovered.count !== null && (
                <circle cx={xAt(hoverIndex)} cy={yCount(hovered.count)} r={4} fill={color} stroke={THEME.colors.white} strokeWidth={2} />
              )}
            </g>
          )}
        </svg>
        {hovered !== null && (
          <div
            role="status"
            style={{
              position: "absolute",
              pointerEvents: "none",
              // Flip to the left of the crosshair in the right third so it stays inside the panel.
              ...(tooltipLeft > 66
                ? { right: `calc(${100 - tooltipLeft}% + 12px)` }
                : { left: `calc(${tooltipLeft}% + 12px)` }),
              top: 14,
              background: THEME.colors.neutral.light.textPrimary,
              color: THEME.colors.white,
              fontSize: 12,
              padding: "6px 9px",
              borderRadius: THEME.borderRadius.md,
              whiteSpace: "nowrap",
            }}
          >
            {formatDay(hovered.date)}
            {hovered.dollars !== null ? ` · ${formatDollars(hovered.dollars)}` : ""}
            {hovered.count !== null ? ` · ${hovered.count} ${countUnit}` : ""}
          </div>
        )}
      </div>
      <details style={{ padding: "6px 18px 14px", fontSize: 12.5, color: AXIS_TEXT }}>
        <summary style={{ cursor: "pointer" }}>View as table</summary>
        <table style={{ borderCollapse: "collapse", marginTop: 8 }}>
          <thead>
            <tr>
              <th style={tableHeadStyle("left")}>Run</th>
              <th style={tableHeadStyle("right")}>{countLabel}</th>
              <th style={tableHeadStyle("right")}>{dollarsLabel}</th>
            </tr>
          </thead>
          <tbody>
            {[...points].reverse().map((point) => (
              <tr key={point.date.toISOString()}>
                <td style={tableCellStyle("left")}>{formatDay(point.date)}</td>
                <td style={tableCellStyle("right")}>{point.count === null ? "—" : point.count}</td>
                <td style={tableCellStyle("right")}>{point.dollars === null ? "—" : formatDollars(point.dollars)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  )
}

function tableHeadStyle(align: "left" | "right"): React.CSSProperties {
  return {
    textAlign: align,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: AXIS_TEXT,
    borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  }
}

function tableCellStyle(align: "left" | "right"): React.CSSProperties {
  return {
    textAlign: align,
    padding: "4px 10px",
    fontSize: 12,
    color: THEME.colors.neutral.light.textPrimary,
    fontVariantNumeric: "tabular-nums",
  }
}
